import type { GuestId } from "./crm-guest-conversion";

export type LocationId = string & { readonly __brand: "LocationId" };
export type SchedulingResourceId = string & {
  readonly __brand: "SchedulingResourceId";
};
export type ModalityId = string & { readonly __brand: "ModalityId" };
export type ServiceId = string & { readonly __brand: "ServiceId" };
export type StaffSkillId = string & { readonly __brand: "StaffSkillId" };
export type AvailabilityRuleId = string & {
  readonly __brand: "AvailabilityRuleId";
};
export type AvailabilityExceptionId = string & {
  readonly __brand: "AvailabilityExceptionId";
};
export type AppointmentId = string & { readonly __brand: "AppointmentId" };
export type AppointmentTransitionId = string & {
  readonly __brand: "AppointmentTransitionId";
};
export type AppointmentHoldId = string & {
  readonly __brand: "AppointmentHoldId";
};
export type WaitlistEntryId = string & {
  readonly __brand: "WaitlistEntryId";
};
export type SchedulingRevision = number & {
  readonly __brand: "SchedulingRevision";
};
export type SchedulingTimezone = string & {
  readonly __brand: "SchedulingTimezone";
};
export type SchedulingIntegrityHash = string & {
  readonly __brand: "SchedulingIntegrityHash";
};

export const schedulingCatalogStatuses = ["ACTIVE", "ARCHIVED"] as const;
export type SchedulingCatalogStatus =
  (typeof schedulingCatalogStatuses)[number];

export const schedulingResourceKinds = [
  "ROOM",
  "STATION",
  "STAFF",
  "EQUIPMENT",
  "SHARED_FACILITY",
] as const;
export type SchedulingResourceKind = (typeof schedulingResourceKinds)[number];

export const availabilityScopeKinds = ["LOCATION", "RESOURCE"] as const;
export type AvailabilityScopeKind = (typeof availabilityScopeKinds)[number];

export const availabilityExceptionEffects = [
  "AVAILABLE",
  "UNAVAILABLE",
] as const;
export type AvailabilityExceptionEffect =
  (typeof availabilityExceptionEffects)[number];

export const appointmentStatuses = [
  "REQUESTED",
  "HELD",
  "CONFIRMED",
  "CHECKED_IN",
  "IN_PROGRESS",
  "COMPLETED",
  "CHECKOUT_PENDING",
  "CHECKED_OUT",
  "CANCELLED",
  "NO_SHOW",
  "ARCHIVED",
] as const;
export type AppointmentStatus = (typeof appointmentStatuses)[number];

export const appointmentTransitionKinds = [
  "STATUS",
  "RESCHEDULE",
  "RESIZE",
] as const;
export type AppointmentTransitionKind =
  (typeof appointmentTransitionKinds)[number];

export const appointmentHoldStatuses = [
  "ACTIVE",
  "CONSUMED",
  "RELEASED",
  "EXPIRED",
] as const;
export type AppointmentHoldStatus = (typeof appointmentHoldStatuses)[number];

export const waitlistStatuses = [
  "WAITING",
  "OFFERED",
  "BOOKED",
  "CANCELLED",
  "EXPIRED",
] as const;
export type WaitlistStatus = (typeof waitlistStatuses)[number];

export const schedulingConflictClaimKinds = [
  "LOCATION",
  "RESOURCE",
  "MODALITY",
] as const;
export type SchedulingConflictClaimKind =
  (typeof schedulingConflictClaimKinds)[number];

export const schedulingDurationPresets = [30, 60, 90] as const;
export type SchedulingDurationPreset =
  (typeof schedulingDurationPresets)[number];
export const schedulingDurationIncrementMinutes = 15;
export const schedulingMinimumDurationMinutes = 15;
export const schedulingMaximumDurationMinutes = 480;
export const schedulingDefaultTimezone =
  "Pacific/Honolulu" as SchedulingTimezone;
export const publicAvailabilityCacheMaxAgeSeconds = 30;
export const publicAvailabilityStaleWhileRevalidateSeconds = 30;

export interface SchedulingBuffers {
  readonly setupMinutes: number;
  readonly cleanupMinutes: number;
  readonly staffMinutes: number;
  readonly travelMinutes: number;
}

export interface LocationRecord {
  readonly id: LocationId;
  readonly organizationId: string;
  readonly displayName: string;
  readonly publicLabel: string;
  readonly timezone: SchedulingTimezone;
  readonly publicVisible: boolean;
  readonly status: SchedulingCatalogStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SchedulingResourceRecord {
  readonly id: SchedulingResourceId;
  readonly organizationId: string;
  readonly locationId: LocationId | null;
  readonly kind: SchedulingResourceKind;
  readonly displayLabel: string;
  readonly staffUserId: string | null;
  readonly status: SchedulingCatalogStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ModalityRecord {
  readonly id: ModalityId;
  readonly organizationId: string;
  readonly displayName: string;
  readonly publicLabel: string;
  readonly exclusiveConflict: boolean;
  readonly publicVisible: boolean;
  readonly status: SchedulingCatalogStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ServiceRecord {
  readonly id: ServiceId;
  readonly organizationId: string;
  readonly displayName: string;
  readonly publicLabel: string;
  readonly defaultDurationMinutes: number;
  readonly buffers: SchedulingBuffers;
  readonly minimumLeadMinutes: number;
  readonly maximumAdvanceDays: number;
  readonly cancellationCutoffMinutes: number;
  readonly publicBookable: boolean;
  readonly status: SchedulingCatalogStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface StaffSkillRecord {
  readonly id: StaffSkillId;
  readonly organizationId: string;
  readonly displayLabel: string;
  readonly status: SchedulingCatalogStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface ServiceResourceRequirement {
  readonly organizationId: string;
  readonly serviceId: ServiceId;
  readonly resourceKind: SchedulingResourceKind;
  readonly quantity: number;
}

export interface AvailabilityRuleRecord {
  readonly id: AvailabilityRuleId;
  readonly organizationId: string;
  readonly scopeKind: AvailabilityScopeKind;
  readonly locationId: LocationId | null;
  readonly resourceId: SchedulingResourceId | null;
  readonly serviceId: ServiceId | null;
  readonly weekday: number;
  readonly localStartMinute: number;
  readonly localEndMinute: number;
  readonly validFrom: string;
  readonly validThrough: string | null;
  readonly status: SchedulingCatalogStatus;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AvailabilityExceptionRecord {
  readonly id: AvailabilityExceptionId;
  readonly organizationId: string;
  readonly scopeKind: AvailabilityScopeKind;
  readonly locationId: LocationId | null;
  readonly resourceId: SchedulingResourceId | null;
  readonly serviceId: ServiceId | null;
  readonly localDate: string;
  readonly localStartMinute: number;
  readonly localEndMinute: number;
  readonly effect: AvailabilityExceptionEffect;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SchedulingInterval {
  readonly startsAt: string;
  readonly endsAt: string;
}

export interface SchedulingConflictClaim extends SchedulingInterval {
  readonly kind: SchedulingConflictClaimKind;
  readonly targetId: string;
}

export interface AppointmentRecord extends SchedulingInterval {
  readonly id: AppointmentId;
  readonly organizationId: string;
  readonly guestId: GuestId;
  readonly serviceId: ServiceId;
  readonly locationId: LocationId;
  readonly sourceHoldId: AppointmentHoldId | null;
  readonly status: AppointmentStatus;
  readonly revision: SchedulingRevision;
  readonly timezone: SchedulingTimezone;
  readonly durationMinutes: number;
  readonly buffers: SchedulingBuffers;
  readonly occupancyStartsAt: string;
  readonly occupancyEndsAt: string;
  readonly conflictSetHash: SchedulingIntegrityHash;
  readonly idempotencyKeyHash: SchedulingIntegrityHash;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface AppointmentTransitionRecord {
  readonly id: AppointmentTransitionId;
  readonly organizationId: string;
  readonly appointmentId: AppointmentId;
  readonly kind: AppointmentTransitionKind;
  readonly fromStatus: AppointmentStatus;
  readonly toStatus: AppointmentStatus;
  readonly fromStartsAt: string;
  readonly toStartsAt: string;
  readonly fromEndsAt: string;
  readonly toEndsAt: string;
  readonly revision: SchedulingRevision;
  readonly actorUserId: string;
  readonly correlationId: string;
  readonly idempotencyKeyHash: SchedulingIntegrityHash;
  readonly occurredAt: string;
  readonly createdAt: string;
}

export interface AppointmentHoldRecord extends SchedulingInterval {
  readonly id: AppointmentHoldId;
  readonly organizationId: string;
  readonly serviceId: ServiceId;
  readonly locationId: LocationId;
  readonly timezone: SchedulingTimezone;
  readonly durationMinutes: number;
  readonly occupancyStartsAt: string;
  readonly occupancyEndsAt: string;
  readonly status: AppointmentHoldStatus;
  readonly revision: SchedulingRevision;
  readonly conflictKeyHash: SchedulingIntegrityHash;
  readonly requestFingerprintHash: SchedulingIntegrityHash;
  readonly expiresAt: string;
  readonly consumedByAppointmentId: AppointmentId | null;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface WaitlistEntryRecord {
  readonly id: WaitlistEntryId;
  readonly organizationId: string;
  readonly guestId: GuestId;
  readonly serviceId: ServiceId;
  readonly locationId: LocationId | null;
  readonly modalityId: ModalityId | null;
  readonly desiredStartDate: string;
  readonly desiredEndDate: string;
  readonly durationMinutes: number;
  readonly status: WaitlistStatus;
  readonly revision: SchedulingRevision;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PublicAvailabilityQuery {
  readonly locationId: LocationId;
  readonly serviceId: ServiceId;
  readonly modalityId: ModalityId | null;
  readonly startDate: string;
  readonly endDate: string;
  readonly durationMinutes: number;
}

export interface PublicAvailabilitySlot {
  readonly date: string;
  readonly locationId: LocationId;
  readonly serviceId: ServiceId;
  readonly modalityId: ModalityId | null;
  readonly startAt: string;
  readonly durationMinutes: number;
  readonly availability: "AVAILABLE" | "LIMITED";
}

export interface PublicAvailabilityResponse {
  readonly generatedAt: string;
  readonly expiresAt: string;
  readonly slots: readonly PublicAvailabilitySlot[];
}

export interface PublicAvailabilityCacheContract {
  readonly key: string;
  readonly maxAgeSeconds: number;
  readonly staleWhileRevalidateSeconds: number;
}

export interface AppointmentCreateCommand extends SchedulingInterval {
  readonly appointmentId: AppointmentId;
  readonly organizationId: string;
  readonly guestId: GuestId;
  readonly serviceId: ServiceId;
  readonly locationId: LocationId;
  readonly holdId: AppointmentHoldId | null;
  readonly timezone: SchedulingTimezone;
  readonly durationMinutes: number;
  readonly buffers: SchedulingBuffers;
  readonly conflictClaims: readonly SchedulingConflictClaim[];
  readonly conflictSetHash: SchedulingIntegrityHash;
  readonly idempotencyKeyHash: SchedulingIntegrityHash;
  readonly correlationId: string;
  readonly actorUserId: string;
  readonly requestedAt: string;
}

export type AppointmentCreateOutcome =
  | {
      readonly kind: "confirmed" | "replayed";
      readonly appointment: AppointmentRecord;
    }
  | { readonly kind: "hold-expired" }
  | { readonly kind: "conflict" }
  | { readonly kind: "rejected" };
